import Head from 'expo-router/head';
import { useEffect } from 'react';
import { APP_NAME } from '@/constants/config';
import { Platform } from 'react-native';
import { markAppReady } from '@/lib/appReady';
import { publicGuidePageStructuredData } from '@/lib/docsLearnStructuredData';
import { GUIDE_DATE_PUBLISHED, GUIDE_DATE_MODIFIED } from '@/constants/guideSeoDates';
import { resolveShareImage, getFallbackShareCard, getPageShareVideo } from '@/lib/socialShareCards';

/**
 * Enhanced MetaHead component for comprehensive SEO.
 *
 * Renders:
 *   - Primary meta (title, description, keywords, author, robots)
 *   - Canonical
 *   - Open Graph (article fields when type="article")
 *   - Twitter Card
 *   - JSON-LD structured data (default Organization, or custom)
 *   - RSS feed discovery
 *
 * @param {string} title - Page title
 * @param {string} description - Page description (truncated to ~300 chars; aim for <=160)
 * @param {string} canonical - Canonical URL path (relative; baseUrl is prepended)
 * @param {string} [image] - OG image URL (absolute)
 * @param {string} [imageAlt] - Description of the preview artwork
 * @param {number} [imageWidth] - OG image width in pixels
 * @param {number} [imageHeight] - OG image height in pixels
 * @param {string} [type] - OG type (website, article, product, etc.)
 * @param {object|null} [video] - Open Graph video; null disables the page default
 * @param {string} video.url - Absolute video file URL
 * @param {string} [video.type] - MIME type, e.g. video/mp4
 * @param {number} [video.width]
 * @param {number} [video.height]
 * @param {string} [keywords] - Comma-separated keywords
 * @param {object} [structuredData] - JSON-LD object. If `guide` is set this is computed automatically.
 * @param {boolean} [noindex] - Whether to noindex the page
 * @param {object} [article] - Article-specific OG meta (for type="article")
 * @param {string} [article.publishedTime] - ISO 8601 date
 * @param {string} [article.modifiedTime] - ISO 8601 date
 * @param {string} [article.author] - Author name
 * @param {string} [article.section] - Article section/category
 * @param {string[]} [article.tags] - Array of tag strings
 * @param {string} [rssUrl] - RSS feed URL to advertise via <link> tag
 * @param {string} [amphtml] - AMP Web Story URL for rel="amphtml" discovery
 * @param {string} [lastModified] - ISO 8601 date for `og:updated_time` and JSON-LD freshness
 * @param {string} [themeColor] - Browser theme-color hex
 *
 * @param {object} [guide] - Shortcut: when set, MetaHead auto-builds a rich
 *                          docs/learn JSON-LD graph via `publicGuidePageStructuredData`
 *                          using `title`/`description`/`canonical`/`keywords` from the same call.
 *                          Avoids duplicating those strings across every page.
 * @param {'docs'|'learn'} guide.type
 * @param {number} [guide.readMinutes]
 * @param {'Beginner'|'Intermediate'|'Advanced'} [guide.difficulty]
 * @param {string} [guide.articleSection]
 * @param {string|string[]} [guide.about]
 * @param {string} [guide.audience]
 * @param {string[]} [guide.teaches]
 * @param {Array<{question:string, answer:string}>} [guide.faqs]
 * @param {Array<{name:string, text:string, image?:string}>} [guide.howToSteps]
 * @param {string} [guide.howToName]
 * @param {string} [guide.datePublished]
 * @param {string} [guide.dateModified]
 */
const MetaHead = ({
  title,
  description,
  canonical,
  image: suppliedImage,
  imageAlt,
  imageWidth,
  imageHeight,
  type = 'website',
  video: suppliedVideo,
  keywords,
  structuredData,
  noindex = false,
  article,
  rssUrl,
  amphtml,
  lastModified,
  themeColor,
  guide,
}) => {
  const fullTitle = title
    ? title.includes(APP_NAME)
      ? title
      : `${title} | ${APP_NAME}`
    : `${APP_NAME} - Ask Anything. Create Anything.`;
  const baseUrl = 'https://gab.ai';
  const canonicalUrl = canonical ? `${baseUrl}${canonical}` : null;
  const image = resolveShareImage(canonical, suppliedImage);
  const fallbackCard = getFallbackShareCard(canonical);
  const resolvedImageAlt = imageAlt || (image === fallbackCard.image ? fallbackCard.alt : fullTitle);
  const video = suppliedVideo === undefined
    ? (noindex ? null : getPageShareVideo(canonical))
    : suppliedVideo;

  // Tells the server-injected #seo-prerender block it can retire — see
  // lib/appReady.js and backend/utils/seoRenderer.js.
  //
  // MetaHead is the right place for this because it is structurally tied to real
  // content: screens render it in their loaded branch, and every loading branch
  // returns its skeleton *before* reaching it (verified across the SEO surfaces —
  // prompts/[slug], tools/[slug], GuidePageScreen). No skeleton or placeholder
  // component renders MetaHead, so this cannot fire on an empty page.
  //
  // Declared above the Platform early-return so the hook order stays stable.
  useEffect(() => {
    markAppReady();
  }, []);

  if (Platform.OS !== 'web') {
    return null;
  }

  // Trim description for the SERP-facing <meta description>. Google typically
  // shows 155–160 chars; we soft-cap at 300 to keep schema/OG flexible.
  const serpDescription = typeof description === 'string'
    ? description.length > 300
      ? `${description.slice(0, 297).trimEnd()}...`
      : description
    : '';

  // Default structured data for organization
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://gab.ai#organization',
    name: 'Gab AI',
    url: baseUrl,
    logo: 'https://cdn.gab.ai/static/media/gab-ai-logo.png',
    description:
      'Type or speak any request. One AI that generates images, videos, music, writes code, analyzes files, and more. Private and powerful.',
    sameAs: [
      'https://gab.com',
      'https://x.com/Gab__AI',
      'https://www.youtube.com/@GabdotAI',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'ai@gab.com',
      contactType: 'customer support',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '1143 Northern Blvd #158',
      addressLocality: 'Clarks Summit',
      addressRegion: 'PA',
      postalCode: '18411',
      addressCountry: 'US',
    },
  };

  // Compute structured data automatically for docs/learn pages.
  let schemaData = structuredData;
  if (!schemaData && guide && guide.type && canonical) {
    schemaData = publicGuidePageStructuredData({
      canonical,
      title: fullTitle,
      description,
      guideType: guide.type,
      keywords,
      image,
      readMinutes: guide.readMinutes,
      difficulty: guide.difficulty,
      articleSection: guide.articleSection,
      about: guide.about,
      audience: guide.audience,
      datePublished: guide.datePublished || GUIDE_DATE_PUBLISHED,
      dateModified: guide.dateModified || lastModified || GUIDE_DATE_MODIFIED,
      teaches: guide.teaches,
      faqs: guide.faqs,
      howToSteps: guide.howToSteps,
      howToName: guide.howToName,
    });
  }
  if (!schemaData) schemaData = defaultStructuredData;

  // `<` is escaped so a stray "</script>" inside any string value cannot close
  // the block early.
  const jsonLd = JSON.stringify(schemaData).replace(/</g, '\\u003c');

  // Open Graph image type heuristic (we ship .png by default).
  const ogImageType = image && /\.jpe?g($|\?)/i.test(image)
    ? 'image/jpeg'
    : image && /\.webp($|\?)/i.test(image)
      ? 'image/webp'
      : 'image/png';
  const ogImageWidth = imageWidth || 1200;
  const ogImageHeight = imageHeight || 630;

  return (
    <Head>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      {serpDescription ? <meta name="description" content={serpDescription} /> : null}
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="Gab AI Inc." />
      <meta name="publisher" content="Gab AI Inc." />
      <meta name="copyright" content="Gab AI Inc." />
      <meta name="rating" content="General" />
      <meta name="distribution" content="global" />
      <meta name="language" content="English" />
      <meta httpEquiv="content-language" content="en-US" />

      {/* Robots. noindex pairs with `follow`, not `nofollow`: every page that
          sets this is an app, auth or not-found surface whose links still point
          at public pages worth crawling, and `nofollow` throws that away for no
          benefit — it does not make the page any less indexed. */}
      {noindex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      {!noindex && <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}
      {!noindex && <meta name="bingbot" content="index, follow" />}

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* AMP Web Story alternate (when a companion story exists) */}
      {amphtml && <link rel="amphtml" href={amphtml} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={APP_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description ? <meta property="og:description" content={description} /> : null}
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:image:type" content={ogImageType} />
      <meta property="og:image:width" content={String(ogImageWidth)} />
      <meta property="og:image:height" content={String(ogImageHeight)} />
      <meta property="og:image:alt" content={resolvedImageAlt} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:locale" content="en_US" />
      {lastModified && <meta property="og:updated_time" content={lastModified} />}
      {video?.url ? (
        <>
          <meta property="og:video" content={video.url} />
          <meta property="og:video:secure_url" content={video.url} />
          {video.type ? <meta property="og:video:type" content={video.type} /> : null}
          {video.width ? <meta property="og:video:width" content={String(video.width)} /> : null}
          {video.height ? <meta property="og:video:height" content={String(video.height)} /> : null}
          {image ? <meta property="og:video:image" content={image} /> : null}
        </>
      ) : null}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@Gab__AI" />
      <meta name="twitter:creator" content="@Gab__AI" />
      <meta name="twitter:title" content={fullTitle} />
      {description ? <meta name="twitter:description" content={description} /> : null}
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={resolvedImageAlt} />
      {canonicalUrl && <meta name="twitter:url" content={canonicalUrl} />}
      {keywords && <meta name="twitter:label1" content="Topics" />}
      {keywords && <meta name="twitter:data1" content={keywords} />}

      {/* Article-specific OG tags (for blog posts) */}
      {type === 'article' && article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {type === 'article' && article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {type === 'article' && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      {type === 'article' && article?.section && (
        <meta property="article:section" content={article.section} />
      )}
      {type === 'article' && article?.tags?.map((tag) => (
        <meta key={`article-tag-${tag}`} property="article:tag" content={tag} />
      ))}

      {/* RSS Feed discovery */}
      {rssUrl && (
        <link rel="alternate" type="application/rss+xml" title={`${APP_NAME} Blog`} href={rssUrl} />
      )}

      {/* Additional SEO */}
      <meta name="application-name" content={APP_NAME} />
      <meta name="apple-mobile-web-app-title" content={APP_NAME} />
      <meta name="format-detection" content="telephone=no" />
      {themeColor && <meta name="theme-color" content={themeColor} />}

      {/* Structured Data / JSON-LD.

          The payload has to be passed as children, not `dangerouslySetInnerHTML`:
          Head is react-helmet-async, which only keeps `<script>` tags carrying a
          `src` or an `innerHTML`, and silently drops everything else. Children
          are what it maps to `innerHTML`. */}
      <script type="application/ld+json">{jsonLd}</script>
    </Head>
  );
};

export default MetaHead;
